import { QueryCache, QueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { createClient, createConfig, type Client } from '../generated/api/client/index.ts';
import { erApiFeil } from './feil.ts';
import { meNoekkel } from './useMe.ts';

/**
 * API-klienten mot admin-flaten (`react-ts-integration`): den genererte klienten,
 * med admin-tokenet lest friskt per kall via porten. Kun src/api/** rører den
 * genererte koden; skjermene får data gjennom hooks her (useMe osv.), aldri
 * gjennom egne fetch-kall.
 */
export function lagApiKlient(baseUrl: string, hentToken: () => Promise<string | null>, hent: typeof fetch = fetch): Client {
  return createClient(
    createConfig({
      baseUrl,
      fetch: hent,
      // Kontraktens AdminAuth er Bearer; den genererte operasjonen ber om tokenet her.
      auth: async () => (await hentToken()) ?? undefined,
    }),
  );
}

/**
 * Én QueryClient per app; ingen automatisk gjentakelse — 401 og 403 er svar, ikke feil å prøve på nytt.
 *
 * Én 403 er likevel et signal (M4.8, DD-01 presisert 2026-09-25): `insufficient_role` på en
 * lesing betyr at tilgangen er endret siden «meg» ble hentet — et rom kan være tatt mens
 * panelet sto åpent. Da hentes «meg» på nytt, og skallet tegner seg etter den nye tilgangen
 * (et medlem uten rom får skjermen for det) i stedet for å vise feilen på hver side.
 */
export function lagQueryClient(): QueryClient {
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (feil, query) => {
        if (erApiFeil(feil) && feil.code === 'insufficient_role' && query.queryKey[0] !== meNoekkel[0]) void queryClient.invalidateQueries({ queryKey: meNoekkel });
      },
    }),
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false, staleTime: 60_000 },
      mutations: { retry: false },
    },
  });
  return queryClient;
}

const Kontekst = createContext<Client | null>(null);

export const ApiKlientProvider = Kontekst.Provider;

export function useApiKlient(): Client {
  const k = useContext(Kontekst);
  if (!k) throw new Error('[innspel] useApiKlient utenfor ApiKlientProvider.');
  return k;
}
